import { ReactNode } from 'react';
import { IVehicleTile } from '../types/vehicleTile';

export interface AvailabilityInfo {
    transferText: ReactNode | string | null;
    transferType: string | null;
    showEstimatedDates: boolean;
}

const containsAny = (tags: string[], ...values: string[]): boolean => values.some((v) => tags.includes(v));

const addDays = (days: number): Date => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
};

const formatMonthDay = (date: Date): string => `${date.getMonth() + 1}/${date.getDate()}`;

const storeTransferTypes = [
    'coming-soon',
    'test-drive',
    'off-lot',
    'sold',
    'on-hold',
    'shipping-to',
    'available-elsewhere',
];

export const getTileAvailabilityText = (
    vehicle: IVehicleTile,
    showStoreInTransferText = true,
    showEtaInTransferText = true,
): AvailabilityInfo => {
    const {
        isComingSoon,
        isSaleable,
        distance,
        statusCode,
        isReserved,
        isAvailableToTransact,
        transferTags = [],
        transferFee,
        storeName,
        stateAbbreviation,
        transferTimesUnavailable,
        minEstimatedTransferDurationInDays,
        maxEstimatedTransferDurationInDays,
        transferArrivalDayDescription,
    } = vehicle;

    let baseInfo: AvailabilityInfo;

    if (isComingSoon) {
        baseInfo = { transferText: 'Coming soon to', transferType: 'coming-soon', showEstimatedDates: false };
    } else if (
        isSaleable &&
        (distance ?? Number.MAX_VALUE) <= 50 &&
        !containsAny(transferTags, 'FreeTransfer', 'ExpressFreeTransfer')
    ) {
        baseInfo = { transferText: 'Test drive today at', transferType: 'test-drive', showEstimatedDates: false };
    } else {
        baseInfo = getTransferInfoFromStatusCode(
            statusCode,
            isReserved,
            isAvailableToTransact,
            transferTags,
            transferFee,
        );
    }

    let transferText = baseInfo.transferText;

    if (transferArrivalDayDescription?.toLowerCase() === 'today') {
        return {
            ...baseInfo,
            transferText: (
                <>
                    Ready for you <strong>{transferArrivalDayDescription}</strong>
                </>
            ),
        };
    } else if (transferArrivalDayDescription?.toLowerCase() === 'tomorrow') {
        return {
            ...baseInfo,
            transferText: (
                <>
                    Get it <strong>{transferArrivalDayDescription}</strong>
                </>
            ),
        };
    } else if (transferArrivalDayDescription) {
        return {
            ...baseInfo,
            transferText: (
                <>
                    Get it by <strong>{transferArrivalDayDescription}</strong>
                </>
            ),
        };
    }

    if (
        showStoreInTransferText &&
        transferText !== null &&
        baseInfo.transferType !== null &&
        storeTransferTypes.includes(baseInfo.transferType) &&
        storeName &&
        stateAbbreviation
    ) {
        transferText += ` CarMax ${storeName}, ${stateAbbreviation}`;
    }

    if (showEtaInTransferText && baseInfo.showEstimatedDates && transferText !== null) {
        let etaString = '';

        if (transferTimesUnavailable) {
            etaString = ' | Est. arrival unavailable';
        } else {
            const transferTimeMin = minEstimatedTransferDurationInDays ?? null;
            const transferTimeMax = maxEstimatedTransferDurationInDays ?? null;

            if (transferTimeMin !== null && transferTimeMax !== null) {
                const minDate = addDays(transferTimeMin);
                const maxDate = addDays(transferTimeMax);
                const startDate = formatMonthDay(minDate);
                const endDate = formatMonthDay(maxDate);
                etaString = ` | Est. arrival ${startDate === endDate ? startDate : `${startDate}-${endDate}`}`;
            }
        }

        transferText += etaString;
    }

    return { ...baseInfo, transferText };
};

const getTransferInfoFromStatusCode = (
    statusCode: number,
    isReserved: boolean | undefined,
    isAvailableToTransact: boolean,
    transferTags: string[] | null = [],
    transferFee: number | undefined,
): AvailabilityInfo => {
    const resolvedTransferTags = transferTags ?? [];
    switch (statusCode) {
        case 320:
        case 720:
            if (isReserved) {
                return { transferText: 'Reserved at', transferType: 'off-lot', showEstimatedDates: false };
            }
            return { transferText: null, transferType: null, showEstimatedDates: false };
        case 325:
        case 725:
            return { transferText: 'Reserved at', transferType: 'off-lot', showEstimatedDates: false };
        case 360:
        case 370:
        case 760:
        case 860:
            return { transferText: 'Sold from', transferType: 'sold', showEstimatedDates: false };
        case 340:
        case 345:
        case 740:
        case 840:
            return { transferText: 'On test drive at', transferType: 'test-drive', showEstimatedDates: false };
        case 347:
        case 350:
        case 747:
        case 750:
        case 847:
        case 850:
            return { transferText: 'Reserved at', transferType: 'on-hold', showEstimatedDates: false };
        case 330:
        case 730:
        case 830:
            if (!isAvailableToTransact) {
                return { transferText: 'Reserved at', transferType: 'on-hold', showEstimatedDates: false };
            }
            break;
        case 100:
        case 640:
        case 650:
        case 810:
            return { transferText: 'Reserved at', transferType: 'shipping-to', showEstimatedDates: true };
        case 0:
            break;
        default:
            return { transferText: null, transferType: null, showEstimatedDates: false };
    }

    if (resolvedTransferTags.includes('SameLocation')) {
        return {
            transferText: 'Available at your store',
            transferType: 'available-at-your-store',
            showEstimatedDates: false,
        };
    }
    if (
        !resolvedTransferTags.length ||
        containsAny(resolvedTransferTags, '', 'TransferFeeStoreMismatch', 'NonTransferableVehicle')
    ) {
        return {
            transferText: 'Only available at',
            transferType: 'available-elsewhere',
            showEstimatedDates: false,
        };
    }
    if (containsAny(resolvedTransferTags, 'FreeTransfer', 'ExpressFreeTransfer')) {
        const isExpress = resolvedTransferTags.includes('ExpressFreeTransfer');
        return {
            transferText: `Free${isExpress ? ' Express ' : ' '}Shipping`,
            transferType: 'shipping-from',
            showEstimatedDates: true,
        };
    }
    if (containsAny(resolvedTransferTags, 'Paid', 'PlusTax')) {
        const isPlusTax = resolvedTransferTags.includes('PlusTax');
        return {
            transferText: `$${transferFee}${isPlusTax ? ' (+ Tax) ' : ' '}Shipping`,
            transferType: 'shipping-from',
            showEstimatedDates: true,
        };
    }

    return { transferText: null, transferType: null, showEstimatedDates: false };
};
